import { createElement } from 'react';
import {
  useCustomMutation,
  useInvalidate,
  useNotification,
  useTranslate,
} from '@refinedev/core';
import { App } from 'antd';
import type { ModStatus, ModStatusBulkOut } from '@/api/generated/schemas';
import { ModStatusRefusalGroups } from '@/components/user_catalog/ModStatusRefusalGroups';
import {
  groupIdRefusals,
  MOD_STATUS_BULK_LIMIT,
  refusedIds,
} from '@/components/user_catalog/modStatusBulk';

/** What a finished request changed and what the backend refused. */
export interface BulkModStatusOutcome {
  updated: number[];
  refused: number[];
}

interface UseBulkModStatusProps {
  /** Refine resource string, e.g. `user_catalog.tags` */
  resource: string;
  /** Names a refused id in the result modal; `#id` without one. */
  labelOf?: (id: number) => string | null | undefined;
}

interface UseBulkModStatusResult {
  /** Ask for confirmation, then PATCH `{resource}/update_status`. */
  update: (
    ids: number[],
    modStatus: ModStatus,
    onSuccess?: (outcome: BulkModStatusOutcome) => void
  ) => void;
  isLoading: boolean;
}

/**
 * Bulk-set `mod_status` on one or many rows of a user_catalog resource.
 * Backend endpoint: `PATCH /api/user_catalog/{resource}/update_status`
 * with body `{ ids, mod_status }` (BulkModStatusIn, at most
 * `MOD_STATUS_BULK_LIMIT` unique ids).
 *
 * Not all-or-nothing: the backend saves every object its rules allow and
 * answers `{updated, refused}`; an id already at the status is in neither.
 * Refusals open a modal with the reasons — a toast cannot hold 50 names.
 *
 * Shared by the List (row selection) and Show (single record) views.
 */
export const useBulkModStatus = ({
  resource,
  labelOf,
}: UseBulkModStatusProps): UseBulkModStatusResult => {
  const t = useTranslate();
  const invalidate = useInvalidate();
  const { open } = useNotification();
  const { modal } = App.useApp();
  const {
    mutate,
    mutation: { isPending },
  } = useCustomMutation<ModStatusBulkOut>();

  // `user_catalog.tags` -> `user_catalog/tags/update_status`
  const url = `${resource.replace('.', '/')}/update_status`;
  const k = (key: string, opts?: Record<string, unknown>) =>
    t(`user_catalog.common.bulk_mod_status.${key}`, opts);

  const report = (
    ids: number[],
    modStatus: ModStatus,
    { updated, refused }: ModStatusBulkOut
  ): BulkModStatusOutcome => {
    const outcome = { updated, refused: refusedIds(ids, refused) };
    if (updated.length > 0) {
      invalidate({ resource, invalidates: ['list', 'detail'] });
    }
    if (outcome.refused.length === 0) {
      open?.({
        type: 'success',
        message: t('notifications.success'),
        description:
          updated.length > 0
            ? k('success', { count: updated.length })
            : k('nothing_changed', {
                count: ids.length,
                status: t(`base.mod_status.${modStatus}`),
              }),
      });
      return outcome;
    }
    const unchanged = ids.length - updated.length - outcome.refused.length;
    const summary = [
      k('result_refused_count', { count: outcome.refused.length }),
      ...(unchanged > 0 ? [k('result_unchanged', { count: unchanged })] : []),
    ].join(' · ');
    (updated.length > 0 ? modal.warning : modal.error)({
      title:
        updated.length > 0
          ? k('result_partial', { updated: updated.length, count: ids.length })
          : k('result_refused', { count: ids.length }),
      content: createElement(ModStatusRefusalGroups, {
        groups: groupIdRefusals(ids, refused, labelOf),
        summary,
        maxHeight: 360,
      }),
      okText: t('actions.close'),
      width: 560,
    });
    return outcome;
  };

  const update = (
    ids: number[],
    modStatus: ModStatus,
    onSuccess?: (outcome: BulkModStatusOutcome) => void
  ) => {
    if (!ids.length) return;
    if (ids.length > MOD_STATUS_BULK_LIMIT) {
      open?.({
        type: 'error',
        message: t('notifications.error'),
        description: k('limit_reached', { limit: MOD_STATUS_BULK_LIMIT }),
      });
      return;
    }

    modal.confirm({
      title: k('confirm', {
        count: ids.length,
        status: t(`base.mod_status.${modStatus}`),
      }),
      okText: k('apply'),
      cancelText: t('actions.cancel'),
      onOk: () => {
        mutate(
          {
            url,
            method: 'patch',
            values: { ids, mod_status: modStatus },
            successNotification: false,
          },
          {
            onSuccess: ({ data }) => onSuccess?.(report(ids, modStatus, data)),
          }
        );
      },
    });
  };

  return { update, isLoading: isPending };
};
